import { useState, useCallback, useMemo, useEffect } from 'react';
import FirebaseUserRepository from '../../users/api/FirebaseUserRepository';
import FirebaseChatRepository from '../../chats/api/FirebaseChatRepository';
import BroadcastMessageToAllUsersUseCase from '../use-cases/BroadcastMessageToAllUsersUseCase';
import SendMessageToSelectedUsersUseCase from '../use-cases/SendMessageToSelectedUsersUseCase';
import GetBirthdayUsersUseCase from '../../users/use-cases/GetBirthdayUsersUseCase';
import GetUsersUseCase from '../../users/use-cases/GetUsersUseCase';

const useMassMessageViewModel = (currentUserId, currentUserName) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [users, setUsers] = useState([]);
    const [birthdayUsers, setBirthdayUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);

    const userRepository = useMemo(() => new FirebaseUserRepository(), []);
    const chatRepository = useMemo(() => new FirebaseChatRepository(), []);

    const broadcastUseCase = useMemo(() => new BroadcastMessageToAllUsersUseCase(userRepository, chatRepository), [userRepository, chatRepository]);
    const sendToSelectedUseCase = useMemo(() => new SendMessageToSelectedUsersUseCase(chatRepository), [chatRepository]);
    const getBirthdayUsersUseCase = useMemo(() => new GetBirthdayUsersUseCase(userRepository), [userRepository]);
    const getUsersUseCase = useMemo(() => new GetUsersUseCase(userRepository), [userRepository]);

    // Load all users and birthday users on mount
    useEffect(() => {
        const load = async () => {
            setUsersLoading(true);
            try {
                const allUsers = await getUsersUseCase.execute();
                setUsers(allUsers);
                
                // Filter birthday users from already loaded users instead of fetching again
                const bUsers = await getBirthdayUsersUseCase.execute();
                setBirthdayUsers(bUsers);
            } catch (err) {
                console.error('Error loading users:', err);
                setError('Failed to load users.');
            } finally {
                setUsersLoading(false);
            }
        };
        load();
    }, [getUsersUseCase, getBirthdayUsersUseCase]);

    const sendMassMessage = useCallback(async (messageText) => {
        if (!currentUserId || !currentUserName) { setError('Admin user details not available.'); return false; }
        if (!messageText.trim()) { setError('Message cannot be empty.'); return false; }
        setLoading(true); setError(null); setSuccess(false);
        try {
            await broadcastUseCase.execute(currentUserId, currentUserName, messageText);
            setSuccess(true);
            return true;
        } catch (err) {
            console.error('Error sending mass message:', err);
            setError('Failed to send mass message.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [currentUserId, currentUserName, broadcastUseCase]);

    const sendToSelected = useCallback(async (selectedUserIds, messageText) => {
        if (!currentUserId || !currentUserName) { setError('Admin user details not available.'); return false; }
        if (!messageText.trim()) { setError('Message cannot be empty.'); return false; }
        if (!selectedUserIds.length) { setError('Please select at least one user.'); return false; }
        setLoading(true); setError(null); setSuccess(false);
        try {
            const targets = users.filter(u => selectedUserIds.includes(u.uid));
            await sendToSelectedUseCase.execute(currentUserId, currentUserName, targets, messageText);
            setSuccess(true);
            return true;
        } catch (err) {
            console.error('Error sending to selected users:', err);
            setError('Failed to send message.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [currentUserId, currentUserName, users, sendToSelectedUseCase]);

    const sendBirthdayMessages = useCallback(async (messageText) => {
        if (!currentUserId || !currentUserName) { setError('Admin user details not available.'); return false; }
        if (!messageText.trim()) { setError('Message cannot be empty.'); return false; }
        if (!birthdayUsers.length) { setError('No users have a birthday today.'); return false; }
        setLoading(true); setError(null); setSuccess(false);
        try {
            await sendToSelectedUseCase.execute(currentUserId, currentUserName, birthdayUsers, messageText);
            setSuccess(true);
            return true;
        } catch (err) {
            console.error('Error sending birthday messages:', err);
            setError('Failed to send birthday messages.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [currentUserId, currentUserName, birthdayUsers, sendToSelectedUseCase]);

    return {
        loading,
        error,
        success,
        users,
        birthdayUsers,
        usersLoading,
        sendMassMessage,
        sendToSelected,
        sendBirthdayMessages,
        setError,
        setSuccess,
    };
};

export default useMassMessageViewModel;
